import { ManualNotificationType } from "@avenueops/shared-types";
import { SmartForm } from "@avenueops/web-components";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import HintIconWithTooltip from "components/HintIconWithTooltip";
import IntlPhoneNumberInput from "components/inputs/IntlPhoneNumberInput";
import CustomSubscriberSignalFormat from "components/monitors/editFormReboot/CustomSubscriberSignalFormat";
import { useFormContext, useWatch } from "react-hook-form";
import { trpc } from "utils/trpc";

interface Props {
  notificationPrefix: string;
  disableCustomize?: boolean;
}

export function SMSNotificationInput({ notificationPrefix, disableCustomize }: Props) {
  const {
    formState: { errors },
    control,
    watch,
  } = useFormContext();
  const notification: ManualNotificationType = useWatch({ name: notificationPrefix });
  const useCustomIntegration = watch(`${notificationPrefix}.useCustomIntegration`);
  const { data: twilioIntegrationData } = trpc.integrations.getTwilioMetadata.useQuery(undefined, { enabled: !!useCustomIntegration });

  return (
    <>
      <div className="col-span-4">
        {useCustomIntegration ? (
          twilioIntegrationData && twilioIntegrationData.fromNumbers.length > 0 ? (
            <>
              <div className="flex w-full gap-1">
                <label htmlFor={`${notificationPrefix}.smsFrom`} className="font-medium text-sm text-gray-950">
                  Phone Number to Send From
                </label>
                <HintIconWithTooltip tooltipText="E.164 format" />
              </div>
              <SmartForm.DropdownSelect
                name={`${notificationPrefix}.smsFrom`}
                placeholder="+14194190034"
                onCreate={(value) => ({
                  label: value,
                  value,
                })}
                options={twilioIntegrationData.fromNumbers.map((phoneNum: string) => ({ label: phoneNum, value: phoneNum }))}
              />
            </>
          ) : (
            <>
              <div className="flex w-full gap-1">
                <label htmlFor={`${notificationPrefix}.smsFrom`} className="font-medium text-sm text-gray-950">
                  Phone Number to Send From
                </label>
                <HintIconWithTooltip tooltipText="E.164 format" />
              </div>
              <SmartForm.TextInput name={`${notificationPrefix}.smsFrom`} placeholder="+14194190034" />
            </>
          )
        ) : null}

        {useCustomIntegration ? (
          twilioIntegrationData && twilioIntegrationData.messagingServices.length > 0 ? (
            <SmartForm.DropdownSelect
              name={`${notificationPrefix}.smsMessagingServiceId`}
              title="Messaging Service ID (Required for US based numbers)"
              placeholder="e.g MGXXXXXXXX"
              onCreate={(value) => ({
                label: value,
                value,
              })}
              options={twilioIntegrationData.messagingServices.map((service) => ({ label: service.friendlyName, value: service.sid }))}
            />
          ) : (
            <SmartForm.TextInput
              name={`${notificationPrefix}.smsMessagingServiceId`}
              title="Messaging Service ID (Required for US based numbers)"
              placeholder="e.g MGXXXXXXXX"
            />
          )
        ) : null}

        <div className="flex w-full gap-1">
          <IntlPhoneNumberInput
            errors={errors}
            title="Phone Number to Notify"
            defaultValue={notification.recipientValue ?? ""}
            fieldName={`${notificationPrefix}.recipientValue`}
          />
        </div>
      </div>

      {disableCustomize ? null : (
        <div className="col-span-6">
          <CustomSubscriberSignalFormat
            control={control}
            notificationPrefix={notificationPrefix}
            notificationType={notification.manualType}
            includeTitle={true}
          />
        </div>
      )}
    </>
  );
}
